import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sidebar } from "../components/layout/Sidebar";
import { useSidebar } from "../contexts/SidebarContext";
import { toast } from "sonner";
import AlertsPanel from "../components/AlertsPanel";
import { CEIScoreDisplay } from "../components/cei/CEIScoreDisplay";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { FounderBadge } from "../components/FounderBadge";
import StripeConnectBanner from "../components/StripeConnectBanner";
import WelcomeOnboardingModal from "../components/dashboard/WelcomeOnboardingModal";
import SubscriptionSuccessModal from "../components/dashboard/SubscriptionSuccessModal";
import { 
  Target,
  TrendingUp,
  TrendingDown,
  Zap,
  Shield,
  Clock,
  AlertTriangle,
  ArrowRight,
  ChevronRight,
  Radio,
  Globe,
  Send,
  FileText,
  Activity,
  Bell,
  Sparkles,
  MessageSquare,
  Users
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

// Execution Flow Stages
const EXECUTION_STAGES = [
  { key: 'acquisition', label: 'ACQUISITION', color: '#3B82F6' },
  { key: 'underwriting', label: 'UNDERWRITING', color: '#6366F1' },
  { key: 'capital', label: 'CAPITAL', color: '#8B5CF6' },
  { key: 'contract', label: 'CONTRACT', color: '#D4AF37' },
  { key: 'closing', label: 'CLOSING', color: '#F59E0B' },
  { key: 'exit', label: 'EXIT', color: '#10B981' },
];

// Map old status to new execution stages
const STATUS_TO_STAGE = {
  'lead': 'acquisition',
  'analyzed': 'underwriting',
  'underwritten': 'capital',
  'under_contract': 'contract',
  'closing': 'closing',
  'closed': 'exit'
};

export default function CommandCenter({ user, subscription, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { mainClass } = useSidebar();
  const [transactions, setTransactions] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [commandInput, setCommandInput] = useState("");
  const [processingCommand, setProcessingCommand] = useState(false);
  const [systemStatus, setSystemStatus] = useState('stable'); // stable, alert, bottleneck
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [commandSuggestions, setCommandSuggestions] = useState([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState(-1);
  const [recentCommands, setRecentCommands] = useState([]);
  const [showAlertsPanel, setShowAlertsPanel] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [alertCount, setAlertCount] = useState(0);
  const [aiMode, setAiMode] = useState(false);
  const [aiResponse, setAiResponse] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [velocity, setVelocity] = useState(null);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showSubscriptionSuccess, setShowSubscriptionSuccess] = useState(false);
  const commandInputRef = useRef(null);
  const canvasRef = useRef(null);

  // Available commands with metadata
  const COMMANDS = [
    { cmd: 'new deal', label: 'New Acquisition', icon: 'Target', path: '/deals', description: 'Add a new property to your pipeline' },
    { cmd: 'run analysis', label: 'Run Analysis', icon: 'Zap', path: null, description: 'Analyze your latest transaction' },
    { cmd: 'execute offer', label: 'Execute Offer', icon: 'Send', path: '/contracts', description: 'Create and send contracts' },
    { cmd: 'market scan', label: 'Market Scan', icon: 'Globe', path: '/deals', description: 'Search active transactions' },
    { cmd: 'capital model', label: 'Capital Model', icon: 'FileText', path: '/calculator', description: 'Run financial scenarios' },
    { cmd: 'execution flow', label: 'Execution Flow', icon: 'Activity', path: '/execution-flow', description: 'View pipeline stages' },
    { cmd: 'network', label: 'Partner Network', icon: 'Users', path: '/network', description: 'Manage execution partners' },
    { cmd: 'deck generator', label: 'Deck Generator', icon: 'Presentation', path: '/deal-packets', description: 'Create pitch decks' },
    { cmd: 'contracts', label: 'Contracts', icon: 'FileSignature', path: '/contracts', description: 'Manage contracts & e-signatures' },
    { cmd: 'underwriting', label: 'Underwriting', icon: 'Calculator', path: '/underwriting', description: 'AI-powered deal analysis' },
    { cmd: 'performance', label: 'Performance', icon: 'BarChart', path: '/performance', description: 'View metrics dashboard' },
    { cmd: 'help', label: 'Help', icon: 'HelpCircle', path: null, description: 'Show available commands' },
  ];

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchData();
    // Load recent commands from localStorage
    const saved = localStorage.getItem('dealforge_recent_commands');
    if (saved) setRecentCommands(JSON.parse(saved).slice(0, 5));
  }, []);

  // Check for first-time user and show welcome onboarding modal
  useEffect(() => {
    if (!loading && transactions !== null) {
      const hasSeenWelcome = localStorage.getItem('hasSeenWelcomeOnboarding');
      const hasDeals = transactions.length > 0;
      
      // Show welcome modal only if user hasn't seen it before and has no deals
      if (!hasSeenWelcome && !hasDeals) {
        const timer = setTimeout(() => {
          setShowWelcomeModal(true);
          localStorage.setItem('hasSeenWelcomeOnboarding', 'true');
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, transactions]);

  // Check for subscription success from Stripe redirect
  useEffect(() => {
    const subscriptionStatus = searchParams.get('subscription');
    if (subscriptionStatus === 'success') {
      // Show celebration modal
      setShowSubscriptionSuccess(true);
      // Clear the URL parameter
      searchParams.delete('subscription');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowCommandPalette(true);
        setTimeout(() => commandInputRef.current?.focus(), 100);
      }
      if (e.key === 'Escape') {
        setShowCommandPalette(false);
        setCommandSuggestions([]);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Update suggestions as user types
  useEffect(() => {
    if (!commandInput.trim()) {
      setCommandSuggestions(recentCommands.length > 0 ? 
        COMMANDS.filter(c => recentCommands.includes(c.cmd)).slice(0, 3) : 
        COMMANDS.slice(0, 5)
      );
      setSelectedSuggestion(-1);
      return;
    }
    
    const query = commandInput.toLowerCase();
    const matches = COMMANDS.filter(c => 
      c.cmd.includes(query) || 
      c.label.toLowerCase().includes(query) ||
      c.description.toLowerCase().includes(query)
    );
    setCommandSuggestions(matches.slice(0, 6));
    setSelectedSuggestion(matches.length > 0 ? 0 : -1);
  }, [commandInput, recentCommands]);

  // Save command to recent
  const saveRecentCommand = (cmd) => {
    const updated = [cmd, ...recentCommands.filter(c => c !== cmd)].slice(0, 5);
    setRecentCommands(updated);
    localStorage.setItem('dealforge_recent_commands', JSON.stringify(updated));
  };

  // Command processing function
  const handleCommand = async (e, selectedCmd = null) => {
    if (e) e.preventDefault();
    
    const cmdToRun = selectedCmd || commandInput.trim().toLowerCase();
    if (!cmdToRun) return;
    
    setProcessingCommand(true);
    setAiResponse(null);
    saveRecentCommand(cmdToRun);
    
    // If AI mode is on, process via AI endpoint
    if (aiMode) {
      try {
        const response = await axios.post(
          `${BACKEND_URL}/api/deals/ai/command`,
          { command: commandInput.trim() },
          { withCredentials: true }
        );
        
        if (response.data.success) {
          setAiResponse(response.data);
          toast.success("AI command processed");
          
          // Navigate if suggested
          if (response.data.navigation) {
            setTimeout(() => navigate(response.data.navigation), 1500);
          }
        } else {
          toast.error("AI could not process the command");
        }
      } catch (error) {
        console.error("AI command error:", error);
        toast.error("Failed to process AI command");
      }
      
      setCommandInput("");
      setProcessingCommand(false);
      return;
    }
    
    // Find matching command (non-AI mode)
    const matchedCommand = COMMANDS.find(c => 
      cmdToRun.includes(c.cmd) || c.cmd.includes(cmdToRun)
    );
    
    if (matchedCommand) {
      if (matchedCommand.cmd === 'run analysis') {
        handleRunAnalysis();
      } else if (matchedCommand.cmd === 'help') {
        toast.info("Commands: new deal, run analysis, execute offer, market scan, capital model, execution flow, network, deck generator");
      } else if (matchedCommand.path) {
        toast.success(`Opening ${matchedCommand.label}...`);
        navigate(matchedCommand.path);
      }
    } else {
      toast.info(`Try: "new deal", "run analysis", "execute offer", "market scan", "capital model", "help"`);
    }
    
    setCommandInput("");
    setShowCommandPalette(false);
    setCommandSuggestions([]);
    setProcessingCommand(false);
  };

  // Handle keyboard navigation in suggestions
  const handleCommandKeyDown = (e) => {
    if (commandSuggestions.length === 0) return;
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestion(prev => 
        prev < commandSuggestions.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestion(prev => 
        prev > 0 ? prev - 1 : commandSuggestions.length - 1
      );
    } else if (e.key === 'Enter' && selectedSuggestion >= 0) {
      e.preventDefault();
      const selected = commandSuggestions[selectedSuggestion];
      handleCommand(null, selected.cmd);
    }
  };

  // Quick action handlers
  const handleNewAcquisition = () => {
    toast.success("Opening Active Transactions - click 'Add Transaction' to create new");
    navigate('/deals');
  };
  
  const handleRunAnalysis = () => {
    if (transactions.length > 0) {
      // Navigate to the first transaction's detail page
      const firstDeal = transactions[0];
      toast.success("Opening analysis for latest transaction...");
      navigate(`/deals/${firstDeal.deal_id}`);
    } else {
      toast.info("No transactions to analyze. Add a new acquisition first.");
      navigate('/deals');
    }
  };

  const handleExecuteOffer = () => {
    if (transactions.length > 0) {
      toast.success("Opening contracts for offer execution...");
      navigate('/contracts');
    } else {
      toast.info("No transactions available. Add a deal first.");
      navigate('/deals');
    }
  };

  const handleMarketScan = () => {
    toast.success("Opening Active Transactions...");
    navigate('/deals');
  };

  const fetchData = async () => {
    try {
      const [dealsRes, metricsRes, contractsRes, activityRes, velocityRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/deals`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals/metrics`, { withCredentials: true }).catch(() => ({ data: null })),
        axios.get(`${BACKEND_URL}/api/contracts`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/deals/activity/recent`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/analytics/velocity`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      
      // Transform deals to transactions with execution stages
      const txs = (dealsRes.data || []).map(deal => {
        // Calculate AI confidence score based on deal data completeness
        const calculateConfidence = () => {
          let score = 50;
          const purchasePrice = deal.purchase_price || deal.asking_price || 0;
          
          if (deal.arv && purchasePrice) {
            score += 15;
            const maxAllowable = deal.arv * 0.7 - (deal.rehab_estimate || 0);
            if (purchasePrice <= maxAllowable) score += 15;
            else if (purchasePrice / deal.arv <= 0.8) score += 8;
          }
          if (deal.rehab_estimate > 0) score += 5;
          if (deal.photos?.length > 0) score += 5;
          
          const stageScores = {
            new_lead: 0, analyzed: 3, underwritten: 6, contracted: 10,
            rehab_estimate: 12, sent_to_title: 14, title_processing: 16,
            ready_to_close: 18, closed: 20, sold: 20
          };
          score += stageScores[deal.pipeline_stage] || stageScores[deal.status] || 0;
          
          return Math.max(0, Math.min(100, Math.round(score)));
        };
        
        const aiScore = deal.ai_score || calculateConfidence();
        const purchasePrice = deal.purchase_price || deal.asking_price || 0;
        
        return {
          ...deal,
          address: deal.property_address?.split(',')[0] || deal.address || 'No Address',
          city: deal.property_address?.split(',')[1]?.trim()?.split(' ')[0] || '',
          state: deal.property_address?.split(',')[1]?.trim()?.split(' ')[1] || deal.property_address?.split(',')[2]?.trim() || '',
          executionStage: STATUS_TO_STAGE[deal.status] || STATUS_TO_STAGE[deal.pipeline_stage] || 'acquisition',
          spread: (deal.arv || 0) - purchasePrice - (deal.rehab_estimate || 0),
          capitalRequired: purchasePrice + (deal.rehab_estimate || 0),
          riskScore: 100 - aiScore,
          aiConfidence: aiScore,
          daysInStage: Math.floor((Date.now() - new Date(deal.updated_at || deal.created_at).getTime()) / (1000 * 60 * 60 * 24))
        };
      });
      
      setTransactions(txs);
      setMetrics(metricsRes.data);
      setContracts(contractsRes.data || []);
      setRecentActivity(activityRes.data || []);
      if (velocityRes.data) setVelocity(velocityRes.data);
      
      // Calculate alert count
      let alerts = 0;
      txs.forEach(tx => {
        if (tx.daysInStage > 14) alerts++;
        if (tx.capitalRequired > 500000 && tx.executionStage === 'contract') alerts++;
      });
      (contractsRes.data || []).forEach(contract => {
        if (contract.status === 'awaiting_signatures') {
          const pending = contract.signatures?.filter(s => s.status === 'pending').length || 0;
          if (pending > 0) alerts++;
        }
      });
      setAlertCount(alerts);
      
      // Determine system status based on data
      const bottlenecks = txs.filter(t => t.daysInStage > 14).length;
      if (bottlenecks > 2) setSystemStatus('bottleneck');
      else if (bottlenecks > 0) setSystemStatus('alert');
      else setSystemStatus('stable');
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate stage metrics
  const getStageMetrics = () => {
    return EXECUTION_STAGES.map(stage => {
      const stageTxs = transactions.filter(t => t.executionStage === stage.key);
      const capital = stageTxs.reduce((sum, t) => sum + (t.capitalRequired || 0), 0);
      const avgDays = stageTxs.length > 0 
        ? stageTxs.reduce((sum, t) => sum + t.daysInStage, 0) / stageTxs.length 
        : 0;
      return {
        ...stage,
        count: stageTxs.length,
        capital,
        avgDays: Math.round(avgDays * 10) / 10,
        velocity: avgDays < 7 ? 'up' : avgDays > 14 ? 'down' : 'stable'
      };
    });
  };

  // Calculate global metrics
  const globalMetrics = {
    capitalInMotion: transactions.reduce((sum, t) => sum + (t.capitalRequired || 0), 0),
    executionVelocity: transactions.length > 0 
      ? transactions.reduce((sum, t) => sum + t.daysInStage, 0) / transactions.length 
      : 0,
    riskExposure: transactions.reduce((sum, t) => sum + (t.riskScore || 0), 0) / Math.max(transactions.length, 1),
    contractsClosing: transactions.filter(t => ['contract', 'closing'].includes(t.executionStage)).length,
    totalTransactions: transactions.length
  };

  const stageMetrics = getStageMetrics();

  // Format relative time helper
  const formatRelativeTime = (time) => {
    if (!time) return '—';
    try {
      const date = new Date(time);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      
      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays}d ago`;
      return date.toLocaleDateString();
    } catch {
      return '—';
    }
  };

  // Canvas animation for capital flow
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || transactions.length === 0) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    let particles = [];
    let animationId;

    const createParticle = () => {
      const stageIdx = Math.floor(Math.random() * (EXECUTION_STAGES.length - 1));
      return {
        x: (stageIdx + 0.5) * (canvas.width / EXECUTION_STAGES.length),
        y: canvas.height / 2 + (Math.random() - 0.5) * 40,
        targetX: (stageIdx + 1.5) * (canvas.width / EXECUTION_STAGES.length),
        progress: 0,
        speed: 0.005 + Math.random() * 0.005,
        color: EXECUTION_STAGES[stageIdx].color
      };
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Draw connection lines
      EXECUTION_STAGES.forEach((stage, idx) => {
        if (idx < EXECUTION_STAGES.length - 1) {
          const x1 = (idx + 0.5) * (canvas.width / EXECUTION_STAGES.length);
          const x2 = (idx + 1.5) * (canvas.width / EXECUTION_STAGES.length);
          ctx.beginPath();
          ctx.moveTo(x1, canvas.height / 2);
          ctx.lineTo(x2, canvas.height / 2);
          ctx.strokeStyle = 'rgba(255,255,255,0.03)';
          ctx.lineWidth = 2;
          ctx.stroke();
        }
      });

      // Manage particles
      if (Math.random() < 0.03 && particles.length < 20) {
        particles.push(createParticle());
      }

      particles = particles.filter(p => {
        p.progress += p.speed;
        if (p.progress >= 1) return false;
        
        const x = p.x + (p.targetX - p.x) * p.progress;
        const y = p.y + Math.sin(p.progress * Math.PI) * 10;
        
        ctx.beginPath();
        ctx.arc(x, y, 2, 0, Math.PI * 2);
        ctx.fillStyle = p.color;
        ctx.globalAlpha = 0.5 * (1 - p.progress);
        ctx.fill();
        ctx.globalAlpha = 1;
        return true;
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();
    return () => cancelAnimationFrame(animationId);
  }, [transactions]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <Sidebar user={user} subscription={subscription} onLogout={onLogout} />
      
      <main className={mainClass}>
        {/* System Overview Strip */}
        <header className="border-b border-white/[0.06] bg-black/80">
          <div className="px-4 md:px-6 py-3 md:py-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              {/* Left: Branding + Status */}
              <div className="flex items-center gap-4 md:gap-6">
                <div>
                  <h1 className="text-base md:text-lg font-semibold tracking-tight">
                    Command <span className="text-[#D4AF37]">Center</span>
                  </h1>
                  <p className="text-[9px] text-white/50 uppercase tracking-[0.15em] hidden md:block">Your deal execution operating system. Track every transaction from acquisition to exit.</p>
                </div>
                
                {/* Live + System Status + Founder Badge */}
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="flex items-center gap-2 px-2 md:px-3 py-1 md:py-1.5 bg-emerald-500/[0.08] border border-emerald-500/20 rounded">
                    <div className="relative w-2 h-2">
                      <div className="absolute inset-0 bg-emerald-400 rounded-full" />
                      <div className="absolute inset-0 bg-emerald-400 rounded-full animate-ping opacity-75" />
                    </div>
                    <span className="text-[10px] font-semibold text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)] uppercase tracking-wider">Live</span>
                  </div>
                  
                  {/* Founder Badge */}
                  <FounderBadge show={user?.has_founder_badge} size="sm" />
                  
                  <div className="hidden md:block">
                    <SystemStatusBadge status={systemStatus} />
                  </div>
                </div>
              </div>

              {/* Center: Key Metrics - Hidden on mobile */}
              <div className="hidden lg:flex items-center gap-8">
                {/* CEI Score - Primary Position */}
                <CEIScoreDisplay variant="header" className="" />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Capital in Motion" 
                  value={`$${(globalMetrics.capitalInMotion / 1000000).toFixed(2)}M`}
                  trend="+12.4%"
                  positive
                  tooltip="Total capital currently deployed across active deals."
                  onClick={() => {
                    toast.info("Viewing all transactions by capital...");
                    navigate('/deals');
                  }}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Velocity" 
                  value={`${globalMetrics.executionVelocity.toFixed(1)}d`}
                  trend="-2.1"
                  positive
                  tooltip="Average speed deals move through the execution pipeline."
                  onClick={() => {
                    toast.info("Viewing execution flow...");
                    navigate('/execution-flow');
                  }}
                />
                <div className="w-px h-10 bg-white/[0.04]" />
                <GlobalMetric 
                  label="Active" 
                  value={globalMetrics.totalTransactions}
                  highlight
                  tooltip="Total number of active transactions in your pipeline."
                  onClick={() => {
                    toast.info("Viewing all active transactions...");
                    navigate('/deals');
                  }}
                />
              </div>

              {/* Right: Alerts + Time */}
              <div className="flex items-center gap-4">
                {/* Alerts Bell */}
                <button
                  onClick={() => setShowAlertsPanel(true)}
                  className="relative p-2 hover:bg-white/[0.02] rounded-2xl transition-all duration-300 group"
                  data-testid="alerts-bell-btn"
                >
                  <Bell className="w-5 h-5 text-white/50 group-hover:text-white/90 transition-all duration-300" />
                  {alertCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#D4AF37] text-black text-[9px] font-bold rounded-full flex items-center justify-center">
                      {alertCount > 9 ? '9+' : alertCount}
                    </span>
                  )}
                </button>
                
                {/* Time */}
                <div className="text-right">
                  <div className="font-mono text-lg text-white/70 tracking-tight">
                    {currentTime.toLocaleTimeString('en-US', { hour12: false })}
                  </div>
                  <div className="text-[9px] text-white/50 uppercase tracking-wider">Eastern</div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Stripe Connect Banner - Show if not connected */}
        <div className="px-4 md:px-6 pt-4">
          <StripeConnectBanner 
            partnerType="investor" 
            apiPrefix="/api/auth"
            compact={true}
          />
        </div>

        {/* Execution Flow Visualization */}
        <section className="border-b border-white/[0.06] bg-black relative ambient-bg overflow-hidden">
          <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none hidden md:block" />
          <div className="px-4 md:px-6 py-4 md:py-6 relative z-10">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Execution Flow</h2>
              <span className="text-[10px] text-white/50 font-mono">
                Total Capital: ${(globalMetrics.capitalInMotion / 1000).toFixed(0)}K
              </span>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
              {stageMetrics.map((stage, idx) => (
                <ExecutionStageCard key={stage.key} stage={stage} index={idx} />
              ))}
            </div>
          </div>
        </section>

        {/* Main Content */}
        <div className="p-4 md:p-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5">
            {/* Left: AI Command Center + Intelligence Widgets */}
            <div className="lg:col-span-3 space-y-4 md:space-y-5 relative z-20">
              {/* AI Command Interface - Primary */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-[#D4AF37]/20 rounded-2xl p-4 relative z-30 overflow-hidden">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                    <span className="text-xs font-semibold text-[#D4AF37]">DealForge AI</span>
                  </div>
                  <span className="text-[9px] text-white/30 font-mono">⌘K</span>
                </div>
                
                {/* Conversation History */}
                {aiResponse && (
                  <div className="mb-3 max-h-48 overflow-y-auto space-y-2">
                    <div className="p-3 bg-[#D4AF37]/5 border border-[#D4AF37]/10 rounded">
                      <p className="text-sm text-white/90 leading-relaxed">{aiResponse.message}</p>
                      
                      {/* Show matching deals if any */}
                      {aiResponse.data?.deals && aiResponse.data.deals.length > 0 && (
                        <div className="space-y-1 mt-3 pt-3 border-t border-white/[0.06]">
                          <span className="text-[10px] text-white/40">Related deals:</span>
                          {aiResponse.data.deals.slice(0, 3).map(deal => (
                            <button
                              key={deal.deal_id}
                              onClick={() => navigate(`/deals/${deal.deal_id}`)}
                              className="w-full text-left px-2 py-1.5 bg-white/[0.02] rounded text-xs text-white/70 hover:bg-[#D4AF37]/10 hover:text-[#D4AF37] transition-all duration-300 flex items-center justify-between"
                            >
                              <span>{deal.address?.split(',')[0]}</span>
                              <span className="text-[#D4AF37]">${(deal.asking_price / 1000).toFixed(0)}K</span>
                            </button>
                          ))}
                        </div>
                      )}
                      
                      {/* Navigation suggestion */}
                      {aiResponse.navigation && (
                        <button
                          onClick={() => navigate(aiResponse.navigation)}
                          className="mt-3 flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#B8972E]"
                        >
                          Take me there <ArrowRight className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )}
                
                <form onSubmit={(e) => { setAiMode(true); handleCommand(e); }} className="relative">
                  <div className="flex items-center gap-2">
                    <input
                      ref={commandInputRef}
                      type="text"
                      value={commandInput}
                      onChange={(e) => setCommandInput(e.target.value)}
                      placeholder="Ask anything..."
                      className="flex-1 min-w-0 bg-white/[0.02] border border-white/[0.06] rounded px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-[#D4AF37]/30 transition-all"
                      disabled={processingCommand}
                    />
                    <button 
                      type="submit"
                      disabled={processingCommand || !commandInput.trim()}
                      className="w-10 h-10 bg-[#D4AF37] rounded flex items-center justify-center transition-all duration-300 hover:bg-[#B8972E] disabled:opacity-50 disabled:hover:bg-[#D4AF37]"
                    >
                      {processingCommand ? (
                        <div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin border-black" />
                      ) : (
                        <Send className="w-4 h-4 text-black" />
                      )}
                    </button>
                  </div>
                </form>
                
                {/* Quick Prompts */}
                {!aiResponse && (
                  <div className="mt-3 space-y-1">
                    <p className="text-[9px] text-white/30 uppercase tracking-wider mb-2">Try asking:</p>
                    {[
                      "What's my highest ROI deal?",
                      "Analyze my pipeline health",
                      "Show deals needing action"
                    ].map((prompt) => (
                      <button
                        key={prompt}
                        onClick={() => {
                          setCommandInput(prompt);
                          setAiMode(true);
                        }}
                        className="w-full text-left px-2 py-1.5 text-[11px] text-white/50 hover:text-[#D4AF37] hover:bg-[#D4AF37]/5 rounded transition-all duration-300"
                      >
                        "{prompt}"
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Capital Summary */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Capital Summary</h3>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-white/50">Deployed</span>
                    <span className="text-sm font-mono text-[#D4AF37]">${(globalMetrics.capitalInMotion / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-white/50">Projected Returns</span>
                    <span className="text-sm font-mono text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]">+${((globalMetrics.capitalInMotion * 0.15) / 1000).toFixed(0)}K</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] text-white/50">Avg ROI</span>
                    <span className="text-sm font-mono font-bold text-white">24.3%</span>
                  </div>
                  <div className="w-full h-1 bg-white/[0.04] rounded-full mt-2">
                    <div className="h-full bg-gradient-to-r from-[#D4AF37] to-emerald-400 rounded-full" style={{ width: '67%' }} />
                  </div>
                  <p className="text-[9px] text-white/40">67% of capital actively deployed</p>
                </div>
              </div>

              {/* Upcoming Deadlines */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Deadlines</h3>
                  <Clock className="w-4 h-4 text-white/30" />
                </div>
                <div className="space-y-2">
                  {transactions.length > 0 ? (
                    transactions.slice(0, 3).map((tx, idx) => (
                      <DeadlineRow 
                        key={tx.deal_id}
                        label={tx.address?.split(',')[0] || 'Deal'}
                        type={tx.executionStage === 'contract' ? 'Closing' : 'Due Diligence'}
                        daysLeft={Math.max(1, 14 - (tx.daysInStage || 0))}
                        urgent={tx.daysInStage > 10}
                        onClick={() => navigate(`/deals/${tx.deal_id}`)}
                      />
                    ))
                  ) : (
                    <p className="text-[11px] text-white/40 text-center py-2">No active deadlines</p>
                  )}
                </div>
              </div>

              {/* CEI Score — Capital Efficiency */}
              <CEIScoreDisplay variant="full" className="" />

              {/* Deal Velocity Score */}
              <VelocityScoreCard velocity={velocity} navigate={navigate} />

              {/* Top Partners — real data */}
              <TopPartnersCard />

              {/* System Status */}
              <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">System Status</h3>
                  <Radio className="w-4 h-4 text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]" />
                </div>
                <div className="space-y-2">
                  <StatusRow label="AI Engine" status="operational" />
                  <StatusRow label="Market Data" status="operational" />
                  <StatusRow label="Integrations" status="operational" />
                </div>
                {user?.is_admin && (
                  <button
                    onClick={async () => {
                      try {
                        const res = await fetch(`${API}/api/deals/attom-backfill/run`, { credentials: "include" });
                        const d = await res.json();
                        alert(`AVM Backfill queued: ${d.queued_count} deals`);
                      } catch {
                        alert("Backfill failed — check logs");
                      }
                    }}
                    className="w-full mt-3 py-1.5 text-[10px] text-[#D4AF37] hover:text-white border border-[#D4AF37]/20 hover:border-white/10 rounded transition-all"
                    data-testid="avm-backfill-btn"
                  >
                    Run AVM Backfill →
                  </button>
                )}
              </div>
            </div>

            {/* Center + Right: Transaction Panels */}
            <div className="lg:col-span-9 relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-sm font-semibold text-white">Active Transactions</h2>
                  <p className="text-[10px] text-white/50">Live capital deployment across execution stages</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded">
                  <div className="relative w-1.5 h-1.5">
                    <div className="absolute inset-0 bg-[#D4AF37] rounded-full" />
                    <div className="absolute inset-0 bg-[#D4AF37] rounded-full animate-ping opacity-50" />
                  </div>
                  <span className="text-[10px] font-semibold text-[#D4AF37]">{transactions.length} LIVE</span>
                </div>
              </div>

              {transactions.length === 0 ? (
                <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-12 text-center">
                  <Target className="w-12 h-12 text-white/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No Active Transactions</h3>
                  <p className="text-sm text-white/70 mb-6">Deploy capital to begin orchestrating execution.</p>
                  <button 
                    onClick={() => navigate('/deals/new')}
                    className="px-6 py-2 bg-[#D4AF37] text-black font-medium rounded hover:bg-[#B8972E] transition-all duration-300"
                  >
                    New Acquisition
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {transactions.slice(0, 6).map((tx) => (
                    <TransactionPanel 
                      key={tx.deal_id} 
                      transaction={tx} 
                      onClick={() => navigate(`/deals/${tx.deal_id}`)}
                    />
                  ))}
                </div>
              )}

              {transactions.length > 6 && (
                <button 
                  onClick={() => navigate('/deals')}
                  className="w-full mt-4 py-3 text-sm text-white/70 hover:text-white border border-white/[0.06] rounded-lg hover:border-white/[0.15] transition-all"
                >
                  View All {transactions.length} Transactions →
                </button>
              )}

              {/* Recent Activity Feed */}
              <div className="mt-6 bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Recent Activity</h3>
                  <Activity className="w-4 h-4 text-white/30" />
                </div>
                <div className="space-y-3">
                  {recentActivity.length > 0 ? (
                    recentActivity.slice(0, 5).map((item, idx) => (
                      <ActivityItem 
                        key={idx}
                        type={item.type}
                        message={item.message}
                        detail={item.detail}
                        time={formatRelativeTime(item.time)}
                        dealId={item.deal_id}
                        contractId={item.contract_id}
                        onNavigate={navigate}
                      />
                    ))
                  ) : (
                    <>
                      <ActivityItem 
                        type="deal"
                        message="No recent activity"
                        detail="Get started by adding a deal"
                        time="—"
                        onNavigate={() => navigate('/deals/new')}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      {/* Alerts Panel */}
      <AlertsPanel 
        isOpen={showAlertsPanel}
        onClose={() => setShowAlertsPanel(false)}
        deals={transactions}
        contracts={contracts}
      />
      
      {/* Welcome Onboarding Modal for First-Time Users */}
      <WelcomeOnboardingModal
        isOpen={showWelcomeModal}
        onClose={() => setShowWelcomeModal(false)}
        userName={user?.name}
      />
      
      {/* Subscription Success Celebration Modal */}
      <SubscriptionSuccessModal
        isOpen={showSubscriptionSuccess}
        onClose={() => setShowSubscriptionSuccess(false)}
        userName={user?.name}
        isFounder={user?.has_founder_badge || user?.pricing_tier === 'beta_founder'}
      />
    </div>
  );
}

// Components

function SystemStatusBadge({ status }) {
  const config = {
    stable: { label: 'System Stable', color: 'emerald' },
    alert: { label: 'System Alert', color: 'amber' },
    bottleneck: { label: 'Bottleneck Detected', color: 'red' }
  };
  const { label, color } = config[status];
  
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 bg-${color}-500/[0.08] border border-${color}-500/20 rounded`}>
      <Shield className={`w-3 h-3 text-${color}-400`} />
      <span className={`text-[10px] text-${color}-400 uppercase tracking-wider`}>{label}</span>
    </div>
  );
}

function GlobalMetric({ label, value, trend, positive, highlight, onClick, tooltip }) {
  const Wrapper = onClick ? 'button' : 'div';
  return (
    <Wrapper 
      onClick={onClick}
      className={`group ${onClick ? 'text-left hover:opacity-90 transition-all cursor-pointer' : ''}`}
      title={tooltip}
    >
      <div className="text-[9px] font-semibold text-white/40 uppercase tracking-[0.15em] mb-1">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-2xl font-mono font-bold tracking-tight transition-all ${
          highlight 
            ? 'text-[#D4AF37] metric-glow-gold' 
            : 'text-white group-hover:text-white/90'
        }`}>
          {value}
        </span>
        {trend && (
          <span className={`flex items-center gap-0.5 text-[10px] font-mono font-semibold ${
            positive 
              ? 'text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.5)]' 
              : 'text-[#FF3B30]'
          }`}>
            {positive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {trend}
          </span>
        )}
      </div>
    </Wrapper>
  );
}

function ExecutionStageCard({ stage, index }) {
  const isLast = index === EXECUTION_STAGES.length - 1;
  
  return (
    <div className="relative group" style={{ animationDelay: `${index * 0.08}s` }}>
      <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4 transition-all duration-400 hover:border-white/[0.15] hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(0,0,0,0.4)] scan-effect animate-fade-in opacity-0" style={{ animationDelay: `${index * 0.08}s`, animationFillMode: 'forwards' }}>
        {/* Glow effect based on stage color */}
        <div 
          className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{ boxShadow: `0 0 40px ${stage.color}20, inset 0 1px 0 ${stage.color}15` }}
        />
        
        <div className="relative flex items-center justify-between mb-3">
          <span 
            className="px-2.5 py-1 rounded-lg text-[9px] font-bold uppercase tracking-wider transition-all duration-300 group-hover:scale-105"
            style={{ 
              color: stage.color, 
              background: `${stage.color}12`,
              boxShadow: `0 0 15px ${stage.color}20`
            }}
          >
            {stage.label}
          </span>
          <AnimatedCounter 
            value={stage.count} 
            className="text-2xl font-mono font-bold text-white"
            duration={1200}
          />
        </div>
        <div className="relative space-y-2">
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-white/50">Capital</span>
            <AnimatedCounter 
              value={stage.capital / 1000} 
              prefix="$" 
              suffix="K" 
              className="font-mono font-semibold text-white"
              duration={1500}
            />
          </div>
          <div className="flex items-center justify-between text-[10px]">
            <span className="text-white/50">Avg Days</span>
            <div className="flex items-center gap-1">
              <AnimatedCounter value={stage.avgDays} className="font-mono font-semibold text-white" duration={1000} />
              {stage.velocity === 'up' && <TrendingUp className="w-3 h-3 text-[#00FF88] drop-shadow-[0_0_8px_rgba(0,255,136,0.5)]" />}
              {stage.velocity === 'down' && <TrendingDown className="w-3 h-3 text-[#FF3B30]" />}
            </div>
          </div>
        </div>
      </div>
      {!isLast && (
        <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 z-10 hidden md:block">
          <ChevronRight className="w-4 h-4 text-white/20 group-hover:text-[#D4AF37]/50 transition-colors duration-300" />
        </div>
      )}
    </div>
  );
}

function TransactionPanel({ transaction, onClick }) {
  const stage = EXECUTION_STAGES.find(s => s.key === transaction.executionStage) || EXECUTION_STAGES[0];
  const isActive = ['contract', 'closing'].includes(transaction.executionStage);
  const spreadPositive = transaction.spread > 0;
  
  return (
    <div 
      onClick={onClick}
      className={`group relative bg-[#0A0A0A]/80 backdrop-blur-sm border rounded-2xl p-5 cursor-pointer transition-all duration-400 hover:-translate-y-2 card-3d ${
        isActive 
          ? 'border-[#D4AF37]/30 shadow-[0_0_30px_rgba(212,175,55,0.1)] hover:shadow-[0_0_50px_rgba(212,175,55,0.2)] hover:border-[#D4AF37]/50' 
          : 'border-white/[0.06] hover:border-white/[0.15] hover:shadow-[0_25px_50px_rgba(0,0,0,0.4)]'
      }`}
    >
      {/* Gradient overlay on hover */}
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      
      {/* Live Pulse */}
      {isActive && (
        <div className="absolute top-4 right-4">
          <div className="relative w-2.5 h-2.5">
            <div className="absolute inset-0 bg-[#D4AF37] rounded-full shadow-[0_0_10px_rgba(212,175,55,0.8)]" />
            <div className="absolute inset-0 bg-[#D4AF37] rounded-full animate-ping opacity-50" />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="relative flex items-center justify-between mb-4">
        <span 
          className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider"
          style={{ 
            color: stage.color, 
            background: `${stage.color}15`,
            boxShadow: `0 0 20px ${stage.color}20`
          }}
        >
          {stage.label}
        </span>
        <div className="flex items-center gap-1 text-[10px] text-white/50">
          <Clock className="w-3 h-3" />
          <span className="font-mono">{transaction.daysInStage}d</span>
        </div>
      </div>

      {/* Address */}
      <h3 className="relative text-base font-semibold text-white mb-1 truncate group-hover:text-white/90 transition-all duration-300">
        {transaction.address || transaction.property_address?.split(',')[0] || 'No Address'}
      </h3>
      <p className="relative text-[11px] text-white/50 mb-5">
        {(transaction.city && transaction.state) ? `${transaction.city}, ${transaction.state}` : 
         (transaction.city || transaction.state) ? (transaction.city || transaction.state) : 
         transaction.property_address?.split(',').slice(1).join(',').trim() || ''}
      </p>

      {/* Metrics Grid */}
      <div className="relative grid grid-cols-2 gap-4 mb-4">
        <div>
          <div className="text-[9px] text-white/40 uppercase tracking-widest mb-1.5">Spread</div>
          <div className={`text-xl font-mono font-bold ${
            spreadPositive 
              ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' 
              : 'text-white/30'
          }`}>
            {spreadPositive ? `+$${(transaction.spread / 1000).toFixed(0)}K` : '—'}
          </div>
        </div>
        <div>
          <div className="text-[9px] text-white/40 uppercase tracking-widest mb-1.5">Capital</div>
          <div className="text-xl font-mono font-bold text-white">
            ${(transaction.capitalRequired / 1000).toFixed(0)}K
          </div>
        </div>
      </div>

      {/* Intelligence Row */}
      <div className="relative pt-4 border-t border-white/[0.06] flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Live Data Indicator */}
          <div className="flex items-center gap-1">
            <div className="relative w-1.5 h-1.5">
              <div className="absolute inset-0 bg-[#00FF88] rounded-full" />
              <div className="absolute inset-0 bg-[#00FF88] rounded-full animate-ping opacity-50" />
            </div>
            <span className="text-[8px] font-bold uppercase tracking-wider text-[#00FF88]/70">LIVE</span>
          </div>
          {/* Risk Score */}
          <div className="flex items-center gap-1.5" title="Risk score updates in real-time based on deal data">
            <div className={`w-1.5 h-1.5 rounded-full ${transaction.riskScore > 40 ? 'bg-[#FF9F0A] shadow-[0_0_8px_rgba(255,159,10,0.6)]' : 'bg-[#00FF88] shadow-[0_0_8px_rgba(0,255,136,0.6)]'}`} />
            <span className="text-[10px] font-mono font-bold text-white/60">Risk {transaction.riskScore}</span>
          </div>
          {/* AI Confidence */}
          {transaction.aiConfidence && (
            <div className="flex items-center gap-1.5" title="AI confidence score - updates as more data is added">
              <Zap className="w-3 h-3 text-[#D4AF37]" />
              <span className="text-[10px] font-mono font-bold text-white/60">{transaction.aiConfidence}%</span>
            </div>
          )}
        </div>
        <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, priority, warning }) {
  return (
    <button 
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 border rounded transition-all group ${
        warning 
          ? 'bg-amber-500/[0.05] hover:bg-amber-500/[0.08] border-amber-500/20 hover:border-amber-500/30' 
          : priority 
            ? 'bg-[#D4AF37]/[0.05] hover:bg-[#D4AF37]/[0.08] border-[#D4AF37]/20 hover:border-[#D4AF37]/30' 
            : 'bg-white/[0.02] hover:bg-white/[0.02] hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.3)] border-white/[0.02] hover:border-[#D4AF37]/20'
      }`}
    >
      <Icon className={`w-4 h-4 transition-all duration-300 ${
        warning 
          ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' 
          : priority 
            ? 'text-[#D4AF37]' 
            : 'text-white/50 group-hover:text-[#D4AF37]'
      }`} />
      <span className={`text-sm transition-all duration-300 ${
        warning 
          ? 'text-amber-300' 
          : priority 
            ? 'text-white/90' 
            : 'text-white/70 group-hover:text-white'
      }`}>{label}</span>
      <ArrowRight className={`w-3 h-3 ml-auto transition-all ${
        warning || priority 
          ? 'opacity-60 group-hover:opacity-100 group-hover:translate-x-1' 
          : 'opacity-0 group-hover:opacity-100 group-hover:translate-x-1 text-white/30'
      }`} />
    </button>
  );
}

function StatusRow({ label, status }) {
  return (
    <div className="flex items-center justify-between py-1">
      <span className="text-[11px] text-white/70">{label}</span>
      <div className="flex items-center gap-1.5">
        <div className={`w-1.5 h-1.5 rounded-full ${status === 'operational' ? 'bg-emerald-400' : 'bg-amber-400'}`} />
        <span className={`text-[9px] font-mono uppercase ${status === 'operational' ? 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]' : 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]'}`}>
          OK
        </span>
      </div>
    </div>
  );
}

function getCeiColor(score) {
  if (score >= 70) return 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]';
  if (score >= 50) return 'text-[#D4AF37]';
  return 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]';
}

// ── Deal Velocity Score Card ───────────────────────────────────────────

function VelocityScoreCard({ velocity, navigate }) {
  if (!velocity) return null;

  const { aggregate_score, aggregate_label, aggregate_color, avg_close_days,
          active_deals_count, deal_velocities } = velocity;

  const slowest = deal_velocities?.slice(0, 2) || [];

  const arcDeg = Math.min(180, (aggregate_score / 200) * 180);

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4"
      data-testid="velocity-score-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider flex items-center gap-2">
          <Zap className="w-3.5 h-3.5 text-[#D4AF37]" /> Deal Velocity
        </h3>
        <button onClick={() => navigate("/deals")}
          className="text-[10px] text-white/30 hover:text-[#D4AF37] transition-colors">
          All Deals →
        </button>
      </div>

      {/* Gauge */}
      <div className="flex flex-col items-center mb-4">
        <div className="relative w-28 h-14 overflow-hidden">
          <svg viewBox="0 0 120 60" className="w-full h-full">
            {/* Track */}
            <path d="M10 60 A50 50 0 0 1 110 60" fill="none" stroke="#1A1A1A" strokeWidth="10" strokeLinecap="round" />
            {/* Arc segments */}
            <path d="M10 60 A50 50 0 0 1 43 17" fill="none" stroke="#FF3B30" strokeWidth="10" strokeOpacity="0.4" strokeLinecap="round" />
            <path d="M43 17 A50 50 0 0 1 77 17" fill="none" stroke="#FF9F0A" strokeWidth="10" strokeOpacity="0.4" strokeLinecap="round" />
            <path d="M77 17 A50 50 0 0 1 110 60" fill="none" stroke="#00FF88" strokeWidth="10" strokeOpacity="0.4" strokeLinecap="round" />
            {/* Needle */}
            <line
              x1="60" y1="60"
              x2={60 + 42 * Math.cos(Math.PI - (arcDeg * Math.PI) / 180)}
              y2={60 - 42 * Math.sin((arcDeg * Math.PI) / 180)}
              stroke={aggregate_color}
              strokeWidth="2.5"
              strokeLinecap="round"
            />
            <circle cx="60" cy="60" r="4" fill={aggregate_color} />
          </svg>
        </div>
        <div className="text-center mt-1">
          <div className="text-2xl font-bold font-mono" style={{ color: aggregate_color }}>
            {aggregate_score}
          </div>
          <div className="text-[11px] font-semibold uppercase tracking-wider" style={{ color: aggregate_color }}>
            {aggregate_label}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="text-center bg-white/[0.03] rounded-lg py-2">
          <div className="text-sm font-bold text-white font-mono">{active_deals_count}</div>
          <div className="text-[9px] text-white/30 uppercase">Active</div>
        </div>
        <div className="text-center bg-white/[0.03] rounded-lg py-2">
          <div className="text-sm font-bold text-white font-mono">{avg_close_days ? `${avg_close_days}d` : "—"}</div>
          <div className="text-[9px] text-white/30 uppercase">Avg Close</div>
        </div>
        <div className="text-center bg-white/[0.03] rounded-lg py-2">
          <div className="text-sm font-bold text-white font-mono">{velocity.closed_deals_count}</div>
          <div className="text-[9px] text-white/30 uppercase">Closed</div>
        </div>
      </div>

      {/* Slowest deals */}
      {slowest.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[9px] text-white/20 uppercase tracking-wider">Needs Attention</p>
          {slowest.map(d => (
            <button key={d.deal_id}
              onClick={() => navigate(`/deals/${d.deal_id}`)}
              className="w-full flex items-center justify-between p-2 bg-white/[0.02] hover:bg-white/[0.05] border border-white/[0.04] rounded-lg transition-all text-left group">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] text-white/60 truncate group-hover:text-white">{d.property_address}</p>
                <p className="text-[9px] text-white/20">{d.pipeline_stage?.replace(/_/g, " ")} · {d.elapsed_days}d elapsed</p>
              </div>
              <span className="text-[10px] font-bold ml-2 px-1.5 py-0.5 rounded flex-shrink-0"
                style={{ color: d.velocity_color, background: `${d.velocity_color}15` }}>
                {d.velocity_label}
              </span>
            </button>
          ))}
        </div>
      )}

      {active_deals_count === 0 && (
        <p className="text-[11px] text-white/20 text-center py-2">Add active deals to track velocity</p>
      )}
    </div>
  );
}

function LeaderboardRow({ rank, name, role, cei }) {

  return (
    <div className="flex items-center justify-between py-1.5">
      <div className="flex items-center gap-2">
        <span className="w-4 text-[10px] font-mono font-bold text-white/40">{rank}.</span>
        <div>
          <span className="text-[11px] text-white/90">{name}</span>
          <span className="text-[9px] text-white/40 ml-1.5">{role}</span>
        </div>
      </div>
      <span className={`text-sm font-mono font-medium ${getCeiColor(cei)}`}>{cei}</span>
    </div>
  );
}


function TopPartnersCard() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading]   = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/api/network/top-partners`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setPartners(d.partners || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="bg-[#0A0A0A]/80 backdrop-blur-sm border border-white/[0.06] rounded-2xl p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">Top Partners</h3>
        <span className="text-[9px] text-[#D4AF37] font-medium">LIVE DATA</span>
      </div>
      {loading ? (
        <div className="flex justify-center py-3">
          <div className="w-4 h-4 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : partners.length === 0 ? (
        <p className="text-[11px] text-white/30 text-center py-2">No partner data yet</p>
      ) : (
        <div className="space-y-2">
          {partners.map((p, i) => (
            <div key={p.partner_id || p.name || `partner-${i}`} className="flex items-center justify-between py-1.5">
              <div className="flex items-center gap-2">
                <span className="w-4 text-[10px] font-mono font-bold text-white/40">{i + 1}.</span>
                <div className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold" style={{ background: `${p.color}20`, color: p.color }}>
                  {p.name?.charAt(0) || "?"}
                </div>
                <div>
                  <span className="text-[11px] text-white/90">{p.name}</span>
                  <span className="text-[9px] text-white/40 ml-1.5">{p.role}</span>
                </div>
              </div>
              <span className={`text-sm font-mono font-medium ${getCeiColor(p.score)}`}>{p.score}</span>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => navigate('/network?tab=directory')}
        className="w-full mt-3 py-2 text-[10px] text-white/50 hover:text-[#D4AF37] border border-white/[0.06] hover:border-[#D4AF37]/20 rounded transition-all"
      >
        View Full Network →
      </button>
    </div>
  );
}



function DeadlineRow({ label, type, daysLeft, urgent, onClick }) {
  return (
    <button 
      onClick={onClick}
      className="w-full flex items-center justify-between py-2 px-2 hover:bg-white/[0.02] rounded transition-all duration-300 group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-white/90 truncate group-hover:text-white">{label}</p>
        <p className="text-[9px] text-white/40">{type}</p>
      </div>
      <div className={`text-right ${urgent ? 'text-[#FF9F0A] drop-shadow-[0_0_8px_rgba(255,159,10,0.5)]' : 'text-white/50'}`}>
        <p className="text-sm font-mono">{daysLeft}d</p>
        <p className="text-[9px]">{urgent ? 'Urgent' : 'left'}</p>
      </div>
    </button>
  );
}

function ActivityItem({ type, message, detail, time, dealId, contractId, onNavigate }) {
  const typeConfig = {
    deal: { icon: Target, color: 'text-[#D4AF37]', bg: 'bg-[#D4AF37]/10' },
    partner: { icon: Users, color: 'text-[#0A84FF] drop-shadow-[0_0_8px_rgba(10,132,255,0.4)]', bg: 'bg-blue-400/10' },
    analysis: { icon: Zap, color: 'text-[#BF5AF2] drop-shadow-[0_0_8px_rgba(191,90,242,0.4)]', bg: 'bg-purple-400/10' },
    contract: { icon: FileText, color: 'text-[#00FF88] drop-shadow-[0_0_10px_rgba(0,255,136,0.5)]', bg: 'bg-emerald-400/10' },
  };
  
  const config = typeConfig[type] || typeConfig.deal;
  const Icon = config.icon;

  const handleClick = () => {
    if (!onNavigate) return;
    if (dealId) onNavigate(`/deals/${dealId}`);
    else if (contractId) onNavigate('/contracts');
    else onNavigate('/deals');
  };

  const isClickable = !!(dealId || contractId || onNavigate);
  
  return (
    <div
      onClick={handleClick}
      className={`flex items-start gap-3 rounded-lg p-1.5 -mx-1.5 transition-all duration-200 ${
        isClickable ? 'cursor-pointer hover:bg-white/[0.03]' : ''
      }`}
      data-testid={`activity-item-${dealId || contractId || 'empty'}`}
    >
      <div className={`w-7 h-7 rounded ${config.bg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-3.5 h-3.5 ${config.color}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-white/90">{message}</p>
        <p className="text-[10px] text-white/40 truncate">{detail}</p>
      </div>
      <span className="text-[9px] text-white/30 flex-shrink-0">{time}</span>
    </div>
  );
}

